import { FaArrowRight } from 'react-icons/fa6'
import { Icon } from '@chakra-ui/icons'
import { Box, Divider, Flex, Image, Text, VStack } from '@chakra-ui/react'
import { formatUnits, parseUnits } from 'viem'

import { DEFAULT_DECIMALS } from '~/constants'
import { formatPercentageString, formatTokenAmount } from '~/helpers'
import { getKeyPressHandlerLimitingDecimals } from '~/helpers/dashboard'
import { useDistributionToken } from '~/hooks/useDistributionToken'
import { useTranslation } from '~/lib/i18n'
import { DeeprStakingI } from '~/types'
import CustomInput from './CustomInput'
import FormBalanceText from './FormBalanceText'
import ModalButton from './ModalButton'
import StakeWithdrawDisclaimer from './StakeWithdrawDisclaimer'

interface Props {
  stakedAmount: number
  hasAlreadyStaked: boolean
  deeprStaking: DeeprStakingI
}

const StakeForm = ({ stakedAmount, hasAlreadyStaked, deeprStaking }: Props) => {
  const { t } = useTranslation('staking')

  const { form, hasAllowance, approve, deposit, isLoadingDeposit, isLoadingApprove, marketStakingInfo } = deeprStaking

  const depositAmount = parseFloat(form.watch('deposit') || 0)

  const potentialStakeAmount = stakedAmount + depositAmount

  const currentPoolShare = stakedAmount / marketStakingInfo.stakedSupply
  const potentialPoolShare = potentialStakeAmount / (marketStakingInfo.stakedSupply + depositAmount)

  const { distributionToken } = useDistributionToken()

  const {
    balance = 0,
    balanceBN = BigInt(0),
    underlyingSymbol = '',
    underlyingDecimals = DEFAULT_DECIMALS,
  } = distributionToken

  const handleBalanceClick = () => {
    const newAmount = formatUnits(balanceBN, underlyingDecimals)

    form.setValue('deposit', newAmount, { shouldValidate: true })
  }

  const handleKeyPressLimitingDecimals = getKeyPressHandlerLimitingDecimals(underlyingDecimals, depositAmount)

  const getCTAButtonMessage = () => {
    if (form.formState.errors.deposit?.message) {
      return form.formState.errors.deposit?.message?.toString()
    }

    if (!hasAllowance) {
      return t('modal.stakeForm.enable')
    }

    return t('modal.header')
  }

  return (
    <VStack alignItems="flex-start" w="full">
      <Flex direction="column" w="full" mb={3}>
        <CustomInput
          register={form.register}
          inputName="deposit"
          registerParams={{
            validate: (value = '0') => {
              if (parseUnits(value, underlyingDecimals) > balanceBN) {
                return t('modal.stakeForm.validation.maxAmountExceeded')
              }

              return true
            },
          }}
          onRightButtonClick={handleBalanceClick}
          rightButtonText={t('modal.stakeForm.rightButtonText')}
          underlyingSymbol={underlyingSymbol}
          onInput={handleKeyPressLimitingDecimals}
        />
        <FormBalanceText label={t('modal.stakeForm.balance')} balance={balance} underlyingSymbol={underlyingSymbol} />
      </Flex>

      <Divider />

      <Flex direction="column" w="full" pt={2} gap={1} fontSize="16px" lineHeight="24px">
        <Flex w="full" justifyContent="space-between">
          <Text fontWeight="medium" color="text.secondary">
            {hasAlreadyStaked ? t('modal.stakeForm.poolShare') : t('modal.stakeForm.yourPoolShare')}
          </Text>

          <Flex alignItems="center" gap={1}>
            {hasAlreadyStaked && depositAmount > 0 && (
              <Flex alignItems="center" gap={1}>
                <Text fontWeight="semibold" color="text.primary">
                  {formatPercentageString(currentPoolShare, 4)}
                </Text>
                <Icon as={FaArrowRight} w="16px" h="14px" color="primary" />
              </Flex>
            )}

            <Text fontWeight="semibold" color="text.primary">
              {formatPercentageString(potentialPoolShare, 4)}
            </Text>
          </Flex>
        </Flex>
        <Flex w="full" justifyContent="space-between" mb={hasAlreadyStaked ? 0 : 4}>
          <Text fontWeight="medium" color="text.secondary">
            {hasAlreadyStaked ? t('modal.stakeForm.totalStake') : t('modal.stakeForm.yourStake')}
          </Text>
          <Flex alignItems="center" gap={1}>
            {hasAlreadyStaked && depositAmount > 0 && (
              <Flex alignItems="center" gap={1}>
                <Text fontWeight="semibold" color="text.primary">
                  {formatTokenAmount(stakedAmount)}
                </Text>
                <Image boxSize={4} src="/assets/deepr-icon.png" />
                <Icon as={FaArrowRight} w="16px" h="14px" color="primary" />
              </Flex>
            )}

            <Text fontWeight="semibold" color="text.primary">
              {formatTokenAmount(potentialStakeAmount)}
            </Text>
            <Image boxSize={4} src="/assets/deepr-icon.png" />
          </Flex>
        </Flex>

        {!hasAlreadyStaked && <StakeWithdrawDisclaimer />}
      </Flex>

      <Box w="full">
        <ModalButton
          onClick={hasAllowance ? deposit : approve}
          isLoading={isLoadingDeposit || isLoadingApprove}
          isDisabled={!!form.formState.errors.deposit || depositAmount <= 0}
          mt={4}
        >
          {getCTAButtonMessage()}
        </ModalButton>
      </Box>
    </VStack>
  )
}

export default StakeForm
